import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Plus,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Settings,
  Phone,
} from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'
import WhatsAppBadge from '@/components/WhatsAppBadge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface WhatsAppConfiguration {
  id: number
  name: string | null
  phone_number_id: string
  display_phone_number: string
  business_account_id: string
  webhook_verify_token: string
  active: boolean
  status: string
  created_at: string
}

interface WhatsAppTemplate {
  id: number
  name: string
  language: string
  category: 'UTILITY' | 'AUTHENTICATION'
  status: 'pending' | 'approved' | 'rejected'
  body_text: string
}

interface WhatsAppProps {
  currentAgent: Agent
  configurations: WhatsAppConfiguration[]
  templates: WhatsAppTemplate[]
}

export default function WhatsApp({
  currentAgent,
  configurations,
  templates,
}: WhatsAppProps) {
  const [syncing, setSyncing] = useState(false)
  const [showNewConfigForm, setShowNewConfigForm] = useState(false)
  const [newConfig, setNewConfig] = useState({
    name: '',
    phone_number_id: '',
    business_account_id: '',
  })

  const handleSyncTemplates = () => {
    setSyncing(true)
    router.post(
      '/settings/whatsapp/sync_templates',
      {},
      {
        preserveScroll: true,
        onSuccess: () => {
          toast.success('Templates sincronizados com sucesso')
          setSyncing(false)
        },
        onError: () => {
          toast.error('Erro ao sincronizar templates')
          setSyncing(false)
        },
      }
    )
  }

  const handleCreateConfig = (e: React.FormEvent) => {
    e.preventDefault()
    router.post(
      '/settings/whatsapp',
      {
        whatsapp_configuration: newConfig,
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          toast.success('Configuração criada com sucesso')
          setShowNewConfigForm(false)
          setNewConfig({ name: '', phone_number_id: '', business_account_id: '' })
        },
        onError: () => {
          toast.error('Erro ao criar configuração')
        },
      }
    )
  }

  const handleDeleteConfig = (configId: number) => {
    if (!confirm('Tem certeza que deseja excluir esta configuração?')) return

    router.delete(`/settings/whatsapp/${configId}`, {
      preserveScroll: true,
      onSuccess: () => {
        toast.success('Configuração excluída com sucesso')
      },
      onError: () => {
        toast.error('Erro ao excluir configuração')
      },
    })
  }

  const handleToggleActive = (config: WhatsAppConfiguration) => {
    router.patch(
      `/settings/whatsapp/${config.id}`,
      {
        whatsapp_configuration: { active: !config.active },
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          toast.success(config.active ? 'Configuração desativada' : 'Configuração ativada')
        },
      }
    )
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Aprovado
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" />
            Rejeitado
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" />
            Pendente
          </span>
        )
    }
  }

  return (
    <>
      <Head title="Configurações do WhatsApp" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/inbox"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="whatsapp" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
              <div className="max-w-4xl space-y-6">
                {/* WhatsApp Configurations */}
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <WhatsAppBadge size="lg" className="text-green-600" />
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          Números de WhatsApp
                        </h2>
                        <p className="text-sm text-gray-500">
                          Configure seus números de WhatsApp Business
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowNewConfigForm(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Adicionar Número
                    </button>
                  </div>

                  {/* New Configuration Form */}
                  {showNewConfigForm && (
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                      <form onSubmit={handleCreateConfig} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nome (opcional)
                            </label>
                            <input
                              type="text"
                              value={newConfig.name}
                              onChange={(e) =>
                                setNewConfig({ ...newConfig, name: e.target.value })
                              }
                              placeholder="Ex: Vendas, Suporte"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phone Number ID *
                            </label>
                            <input
                              type="text"
                              value={newConfig.phone_number_id}
                              onChange={(e) =>
                                setNewConfig({ ...newConfig, phone_number_id: e.target.value })
                              }
                              required
                              placeholder="ID do número no Meta"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Business Account ID *
                            </label>
                            <input
                              type="text"
                              value={newConfig.business_account_id}
                              onChange={(e) =>
                                setNewConfig({
                                  ...newConfig,
                                  business_account_id: e.target.value,
                                })
                              }
                              required
                              placeholder="WABA ID"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setShowNewConfigForm(false)}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            disabled={!newConfig.phone_number_id || !newConfig.business_account_id}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Criar
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Configurations List */}
                  <div className="divide-y divide-gray-100">
                    {configurations.length === 0 ? (
                      <div className="px-6 py-8 text-center text-gray-500">
                        <Phone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>Nenhum número de WhatsApp configurado</p>
                        <p className="text-sm mt-1">
                          Clique em "Adicionar Número" para começar
                        </p>
                      </div>
                    ) : (
                      configurations.map((config) => (
                        <div
                          key={config.id}
                          className="px-6 py-4 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                config.active
                                  ? 'bg-green-100 text-green-600'
                                  : 'bg-gray-100 text-gray-400'
                              }`}
                            >
                              <WhatsAppBadge size="md" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">
                                  {config.name || config.display_phone_number}
                                </span>
                                {config.active ? (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                    Ativo
                                  </span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                    Inativo
                                  </span>
                                )}
                              </div>
                              {config.name && (
                                <p className="text-sm text-gray-500">
                                  {config.display_phone_number}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-0.5">
                                Phone ID: {config.phone_number_id}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleToggleActive(config)}
                                  className={`p-2 rounded-lg transition-colors ${
                                    config.active
                                      ? 'text-yellow-600 hover:bg-yellow-50'
                                      : 'text-green-600 hover:bg-green-50'
                                  }`}
                                >
                                  <Settings className="w-4 h-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {config.active ? 'Desativar' : 'Ativar'}
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleDeleteConfig(config.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Excluir</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Templates Section */}
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        Templates de Mensagens
                      </h2>
                      <p className="text-sm text-gray-500">
                        Templates pré-aprovados pelo WhatsApp para mensagens fora da janela de 24h
                      </p>
                    </div>
                    <button
                      onClick={handleSyncTemplates}
                      disabled={syncing || configurations.length === 0}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                      Sincronizar Templates
                    </button>
                  </div>

                  <div className="divide-y divide-gray-100">
                    {templates.length === 0 ? (
                      <div className="px-6 py-8 text-center text-gray-500">
                        <p>Nenhum template encontrado</p>
                        <p className="text-sm mt-1">
                          Clique em "Sincronizar Templates" para importar do WhatsApp
                        </p>
                      </div>
                    ) : (
                      templates.map((template) => (
                        <div key={template.id} className="px-6 py-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900">
                                  {template.name}
                                </span>
                                {getStatusBadge(template.status)}
                                <span className="text-xs text-gray-400">
                                  {template.category}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                                {template.body_text}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                Idioma: {template.language}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-blue-800 mb-2">
                    Como configurar o WhatsApp Business API
                  </h3>
                  <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Acesse o Meta Business Suite e crie um App de WhatsApp</li>
                    <li>Obtenha o Phone Number ID e Business Account ID</li>
                    <li>Configure o webhook no Meta para apontar para seu servidor</li>
                    <li>Configure o Access Token nas credenciais da aplicação (rails credentials:edit)</li>
                    <li>Crie templates de mensagem no Meta e aguarde aprovação</li>
                  </ol>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  )
}
